/**
 * Uploads a very large file to the backend a few megabytes at a time.
 *
 * The Active Taxpayer List is 300 MB+. Sent as one request it would be cut off
 * by a proxy's body-size limit or the server's request timeout, and a single
 * dropped connection would mean starting again from zero. So the file is sliced
 * in the browser (never read into memory) and each slice is its own request:
 *
 *   POST /atl/upload/init            -> { uploadId, chunkSize, offset }
 *   PUT  /atl/upload/:id/chunk       one slice, at the offset the server expects
 *   POST /atl/upload/:id/complete    -> the import starts
 *
 * THE SERVER DECIDES WHERE THE UPLOAD IS
 * --------------------------------------
 * `offset` is always whatever the server last reported, never what this side
 * believes it sent. That one rule covers every failure:
 *
 *   connection dropped mid-slice   retry -> 409 { offset } -> continue from there
 *   page reloaded, same file again init returns the offset already on disk
 *   proxy says 413 (slice too big) halve the slice size and send again
 *   network down for a while       wait (longer each time) and retry
 */

import API from "./api";

// The smallest slice worth sending: a stock nginx allows 1 MB bodies, and this
// stays under it with room for headers.
const MIN_CHUNK_SIZE = 256 * 1024;
// Tries in a row with no progress before giving up (about a minute of waiting).
const MAX_ATTEMPTS = 7;
const BACKOFF_MS = [500, 1000, 2000, 4000, 8000, 15000, 30000];

const isCancelled = (error) =>
  error?.code === "ERR_CANCELED" || error?.name === "CanceledError" || error?.name === "AbortError" || error?.__CANCEL__ === true;

const defaultWait = (ms, signal) =>
  new Promise((resolve, reject) => {
    const onAbort = () => {
      clearTimeout(timer);
      reject(Object.assign(new Error("Upload cancelled"), { code: "ERR_CANCELED" }));
    };
    const timer = setTimeout(() => {
      signal?.removeEventListener?.("abort", onAbort);
      resolve();
    }, ms);
    if (signal?.aborted) onAbort();
    else signal?.addEventListener?.("abort", onAbort, { once: true });
  });

const serverOffset = (error) => {
  const offset = error?.response?.data?.offset;
  return Number.isSafeInteger(offset) && offset >= 0 ? offset : null;
};

/** The text to show an admin for a failed upload. */
export const uploadErrorMessage = (error) => {
  if (isCancelled(error)) return "Upload cancelled.";
  if (error?.response?.data?.message) return error.response.data.message;
  if (!error?.response) return "Could not reach the server. Check your connection and try again - the upload will continue from where it stopped.";
  return error?.message || "The upload failed.";
};

export const isUploadCancelled = isCancelled;

/**
 * Uploads `file` and starts its import. Resolves with the server's answer to
 * "complete" ({ importId, message }); rejects with the last error otherwise.
 *
 *   onProgress({ uploadId, loaded, total, resumed })   called often
 *   signal                                             AbortSignal to cancel
 *
 * `api` and `wait` exist so tests can run this without a network or a clock.
 */
export const uploadFileInChunks = async ({ file, onProgress, signal, api = API, wait = defaultWait }) => {
  const report = (loaded, extra) => onProgress?.({ loaded: Math.min(loaded, file.size), total: file.size, ...extra });
  const init = await api.post("/atl/upload/init", { fileName: file.name, fileSize: file.size }, { signal });
  const { uploadId } = init.data;
  let chunkSize = init.data.chunkSize;
  let offset = init.data.offset;
  let attempts = 0;
  report(offset, { uploadId, resumed: Boolean(init.data.resumed) });

  // No progress this round: wait, or give up if that has happened too often.
  const stalled = async (error) => {
    attempts += 1;
    if (attempts >= MAX_ATTEMPTS) throw error;
    await wait(BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)], signal);
  };

  for (;;) {
    while (offset < file.size) {
      if (signal?.aborted) throw Object.assign(new Error("Upload cancelled"), { code: "ERR_CANCELED" });
      const start = offset;
      try {
        const response = await api.put(`/atl/upload/${uploadId}/chunk?offset=${start}`, file.slice(start, start + chunkSize), {
          headers: { "Content-Type": "application/octet-stream" },
          signal,
          onUploadProgress: (event) => report(start + (event?.loaded || 0), { uploadId }),
        });
        offset = response.data.offset;
        attempts = 0;
        report(offset, { uploadId });
      } catch (error) {
        if (isCancelled(error)) throw error;
        const status = error?.response?.status;
        const known = serverOffset(error);
        if (status === 409 && known !== null) {
          // The server has a different amount than we thought. Moving forward
          // is progress; being sent back to the same place is not.
          if (known > start) attempts = 0;
          else await stalled(error);
          offset = known;
        } else if (status === 413 && chunkSize > MIN_CHUNK_SIZE) {
          chunkSize = Math.max(MIN_CHUNK_SIZE, Math.floor(chunkSize / 2));
        } else if (status >= 400 && status < 500) {
          // Refused for a reason that sending it again will not change.
          throw error;
        } else {
          await stalled(error);
        }
      }
    }

    try {
      const done = await api.post(`/atl/upload/${uploadId}/complete`, {}, { signal });
      return { uploadId, ...done.data };
    } catch (error) {
      if (isCancelled(error)) throw error;
      const status = error?.response?.status;
      const known = serverOffset(error);
      if (status === 409 && known !== null && known < file.size) {
        // The server is missing the end of the file: go back and send it.
        await stalled(error);
        offset = known;
      } else if (status >= 400 && status < 500) {
        throw error;
      } else {
        await stalled(error);
      }
    }
  }
};
